/**
 * InspectionTimerBar
 * Live HH:MM:SS display with Pause/Resume/Stop buttons and book vs actual comparison.
 */
import { Button } from '@/components/ui/button';
import { Pause, Play, Square, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TimerStatus } from '@/hooks/useInspectionTimer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PAUSE_REASON_OPTIONS, type PauseDetail } from '@/lib/timer/interaction/pauseIntent';

interface InspectionTimerBarProps {
  elapsedSeconds: number;
  timerStatus: TimerStatus;
  bookTimeMin: number | null;
  isMutating: boolean;
  /** Core-100 R4 (CORE-AJ): a pause always says why — one of the shared pause reasons. */
  onPause: (detail: PauseDetail) => void;
  onResume: () => void;
  onStop: () => void;
}

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function getComparisonColor(elapsedMin: number, bookMin: number): string {
  const ratio = elapsedMin / bookMin;
  if (ratio <= 0.8) return 'text-green-600';
  if (ratio <= 1.0) return 'text-yellow-600';
  return 'text-red-600';
}

export function InspectionTimerBar({
  elapsedSeconds,
  timerStatus,
  bookTimeMin,
  isMutating,
  onPause,
  onResume,
  onStop,
}: InspectionTimerBarProps) {
  const elapsedMin = elapsedSeconds / 60;
  const isRunning = timerStatus === 'running';
  const isPaused = timerStatus === 'paused';

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2">
      <Clock className="h-4 w-4 text-muted-foreground" />
      
      {/* Live time display */}
      <span className={cn(
        'font-mono text-lg font-semibold tabular-nums',
        isRunning && 'text-green-600',
        isPaused && 'text-yellow-600',
      )}>
        {formatTime(elapsedSeconds)}
      </span>

      {/* Book vs Actual comparison */}
      {bookTimeMin != null && (
        <span className={cn(
          'text-xs',
          getComparisonColor(elapsedMin, bookTimeMin)
        )}>
          Book: {bookTimeMin}m | Actual: {Math.round(elapsedMin)}m
        </span>
      )}

      <div className="ml-auto flex items-center gap-1">
        {isRunning && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={isMutating} className="h-7 px-2 text-xs" data-testid="inspection-pause">
                <Pause className="h-3 w-3 mr-1" />Pause
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Why are you stopping?</DropdownMenuLabel>
              {PAUSE_REASON_OPTIONS.map((o) => (
                <DropdownMenuItem key={o.detail} onSelect={() => onPause(o.detail)} data-testid={`inspection-pause-${o.detail}`}>
                  {o.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {isPaused && (
          <Button size="sm" variant="outline" onClick={onResume} disabled={isMutating} className="h-7 px-2 text-xs">
            <Play className="h-3 w-3 mr-1" />Resume
          </Button>
        )}
        {(isRunning || isPaused) && (
          <Button size="sm" variant="destructive" onClick={onStop} disabled={isMutating} className="h-7 px-2 text-xs">
            <Square className="h-3 w-3 mr-1" />Stop
          </Button>
        )}
      </div>
    </div>
  );
}
